import type { TabsData } from 'Modulor/MethodGrouping/CategoryTabs';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import IntegrationCommunication from 'services/integration-communication';
import type { UIPaymentGroups, UIPayoutGroups } from 'store/formatters';
import { sortByArrangement } from 'store/formatters';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import type { PayGroupCategories, PayGroupCategory, PayoutGroupCategories } from 'store/models';
import { resetAutoSelectTriggered } from 'store/slices/autoFlow';
import { setCarouselMethods } from 'store/slices/carousel';
import { setSelectedPayGroupId } from 'store/slices/global';
import {
  selectedCategorySelector,
  setSelectedCategory,
  setShowGroupingMethodsInCarousel,
  setUserCategorySelection,
  userSelectionsSelector,
} from 'store/slices/methodGrouping';
import type { CategoryConfig, GroupedMethodsByCategory } from 'utils';
import { DISABLED_CATEGORY_KEY, inIframe, isMethodAvailable, parseCategoryConfig, REGULAR_CATEGORY_KEY } from 'utils';
import { getIsRestrictionActive } from 'utils/getIsMethodActive';

const SCROLL_TIMEOUT_MS = 1000;

type Method = UIPaymentGroups | UIPayoutGroups;

interface CarouselConfig {
  /** Legacy carousel method list (show_in_carousel per method) */
  carouselMethodList?: Method[];
  /** Callback when a method is preselected in carousel */
  onMethodPreselect?: (method: Method) => void;
  /** Block preselect (e.g. when autoSelectTriggered) */
  isAutoSelectBlocked?: boolean;
  /** Method from pay_group URL param — takes priority over first method in preselect */
  methodToRedirect?: Method;
}

interface UseMethodGroupingProps<T extends UIPaymentGroups | UIPayoutGroups> {
  methods: T[];
  paygroupCategories?: PayoutGroupCategories | PayGroupCategories;
  shouldSkipFeature?: boolean;
  /** When provided, hook manages carousel store sync (replaces usePrepareMethodsForCarousel) */
  carousel?: CarouselConfig;
}

interface UseMethodGroupingReturn {
  categoryConfig: CategoryConfig;
  groupedMethodsByCategory: GroupedMethodsByCategory[];
  selectedCategory: string | null;
  setSelectedCategory: (category: string | null) => void;
  carouselTabs: TabsData[];
  handleCategorySelect: (categoryName: string) => void;
  visibleCategories: GroupedMethodsByCategory[];
  disabledCategory: GroupedMethodsByCategory | null;
  saveUserSelection: (categoryName: string, payGroupId: number) => void;
  getUserSelectedGroup: (categoryName: string) => number | null;
}

// ============= TYPE GUARDS =============

export const hasLostProfitInfo = (method: UIPaymentGroups | UIPayoutGroups): method is UIPaymentGroups =>
  'lostProfitInfo' in method;

// ============= BUSINESS LOGIC =============

/**
 * Business rule: Determines if a method is disabled for category placement.
 * A method is disabled if:
 * - Its restriction is still active (restrictedUntil is in the future), OR
 * - It belongs to the disabled category and has no restrictedUntil (permanently disabled), OR
 * - It is not enabled (base availability from limits/wager/backend)
 *
 * A method with an EXPIRED restrictedUntil is active only if enabled is also true
 */
const isMethodDisabled = <T extends UIPaymentGroups | UIPayoutGroups>(method: T): boolean => {
  if (getIsRestrictionActive(method.restrictedUntil)) {
    return true;
  }

  if (!method.enabled) {
    return true;
  }

  if (method.restrictedUntil && !getIsRestrictionActive(method.restrictedUntil)) {
    return false;
  }

  return method.disabledByCategory;
};

/**
 * Business logic: Separates methods into disabled and active
 */
const separateMethodsByDisabledStatus = <T extends UIPaymentGroups | UIPayoutGroups>(methods: T[]) => {
  const disabledMethods: T[] = [];
  const activeMethods: T[] = [];

  methods.forEach((method) => {
    if (isMethodDisabled(method)) {
      disabledMethods.push(method);
    } else {
      activeMethods.push(method);
    }
  });

  return {
    disabledMethods,
    activeMethods,
  };
};

const sortMethodsInCategory = <T extends UIPaymentGroups | UIPayoutGroups>(
  methods: T[],
  categoryPayGroupIds: number[],
): T[] =>
  methods.sort((a, b) => {
    const aIndex = categoryPayGroupIds.indexOf(a.id);
    const bIndex = categoryPayGroupIds.indexOf(b.id);
    return aIndex - bIndex;
  });

const buildRegularCategoryGroups = <T extends UIPaymentGroups | UIPayoutGroups>(
  categories: PayGroupCategory[],
  activeMethods: T[],
): GroupedMethodsByCategory[] => {
  const sortedCategories = [...categories]
    .filter((cat) => cat.category_name !== DISABLED_CATEGORY_KEY)
    .sort((a, b) => a.arrangement - b.arrangement);

  return sortedCategories.map((category) => {
    const categoryMethods = activeMethods.filter((method) => category.pay_group_ids.includes(method.id));
    const sortedMethods = sortMethodsInCategory(categoryMethods, category.pay_group_ids);

    return {
      categoryName: category.category_name,
      arrangement: category.arrangement,
      methods: sortedMethods as UIPaymentGroups[],
    };
  });
};

const sortDisabledMethods = <T extends UIPaymentGroups | UIPayoutGroups>(
  disabledMethods: T[],
  existingDisabledCategory?: PayGroupCategory,
): T[] => {
  if (!existingDisabledCategory) {
    return [...disabledMethods].sort((a, b) => b.arrangement - a.arrangement);
  }

  const categoryOrderMap = new Map(existingDisabledCategory.pay_group_ids.map((id, index) => [id, index]));

  return [...disabledMethods].sort((a, b) => {
    const aInCategory = categoryOrderMap.has(a.id);
    const bInCategory = categoryOrderMap.has(b.id);

    if (aInCategory && bInCategory) {
      return (categoryOrderMap.get(a.id) || 0) - (categoryOrderMap.get(b.id) || 0);
    }
    if (aInCategory) return -1;
    if (bInCategory) return 1;
    return b.arrangement - a.arrangement;
  });
};

const buildDisabledCategoryGroup = <T extends UIPaymentGroups | UIPayoutGroups>(
  disabledMethods: T[],
  categories: PayGroupCategory[],
  existingDisabledCategory?: PayGroupCategory,
): GroupedMethodsByCategory => {
  const maxArrangement = Math.max(...categories.map((cat) => cat.arrangement), 0);
  const disabledArrangement = existingDisabledCategory ? existingDisabledCategory.arrangement : maxArrangement + 1;

  const sortedDisabledMethods = sortDisabledMethods(disabledMethods, existingDisabledCategory);

  return {
    categoryName: DISABLED_CATEGORY_KEY,
    arrangement: disabledArrangement,
    methods: sortedDisabledMethods as UIPaymentGroups[],
  };
};

export const groupMethodsByCategoriesWithDisabled = <T extends UIPaymentGroups | UIPayoutGroups>(
  methods: T[],
  categories: PayGroupCategory[],
): GroupedMethodsByCategory[] => {
  const existingDisabledCategory = categories.find((cat) => cat.category_name === DISABLED_CATEGORY_KEY);

  const { disabledMethods, activeMethods } = separateMethodsByDisabledStatus(methods);

  // Active methods that only exist in the disabled category (e.g. restrictedUntil expired)
  // should fall into PH.REGULAR_METHOD_LIST_CAPTION, or the first regular category if it doesn't exist
  const regularCategories = categories.filter((cat) => cat.category_name !== DISABLED_CATEGORY_KEY);
  const allRegularIds = new Set(regularCategories.flatMap((cat) => cat.pay_group_ids));
  const unplacedActiveIds = activeMethods.filter((m) => !allRegularIds.has(m.id) && m.restrictedUntil).map((m) => m.id);

  let categoriesToBuild = categories;

  if (unplacedActiveIds.length > 0 && regularCategories.length > 0) {
    const regularTarget =
      regularCategories.find((cat) => cat.category_name === REGULAR_CATEGORY_KEY) ||
      regularCategories.sort((a, b) => a.arrangement - b.arrangement)[0];

    categoriesToBuild = categories.map((cat) =>
      cat.category_name === regularTarget.category_name
        ? {
            ...cat,
            pay_group_ids: [...cat.pay_group_ids, ...unplacedActiveIds],
          }
        : cat,
    );
  }

  const regularGroups = buildRegularCategoryGroups(categoriesToBuild, activeMethods);

  if (disabledMethods.length === 0) {
    return regularGroups;
  }

  const disabledGroup = buildDisabledCategoryGroup(disabledMethods, categories, existingDisabledCategory);

  return [...regularGroups, disabledGroup];
};

// ============= CAROUSEL HELPERS =============

const getActiveMethods = (
  selectedCategory: string | null,
  groupedMethodsByCategory: GroupedMethodsByCategory[],
  categories: PayGroupCategories['categories'] | undefined,
  methods: Method[],
): Method[] => {
  if (!selectedCategory) {
    return methods.filter((m) => isMethodAvailable(m));
  }

  const categoryGroup = groupedMethodsByCategory.find(({ categoryName }) => categoryName === selectedCategory);
  if (categoryGroup?.methods.length) {
    return categoryGroup.methods;
  }

  const categoryConfig = categories?.find((cat) => cat.category_name === selectedCategory);
  if (categoryConfig) {
    return methods.filter((m) => categoryConfig.pay_group_ids.includes(m.id) && isMethodAvailable(m));
  }

  return [];
};

const getMethodToPreselect = (
  sortedMethods: Method[],
  selectedCategory: string | null,
  activeMethods: Method[],
  getUserSelectedGroup?: (categoryName: string) => number | null,
  methodToRedirect?: Method,
): Method | null => {
  // pay_group from URL takes highest priority
  if (methodToRedirect && isMethodAvailable(methodToRedirect)) {
    const redirectMethod = activeMethods.find((m) => m.id === methodToRedirect.id);
    if (redirectMethod) return redirectMethod;
  }

  const firstMethod = sortedMethods.find((m) => isMethodAvailable(m)) ?? sortedMethods[0];
  if (!firstMethod) return null;

  const savedMethodId = selectedCategory && getUserSelectedGroup ? getUserSelectedGroup(selectedCategory) : null;
  const savedMethod = savedMethodId ? activeMethods.find((m) => m.id === savedMethodId) : null;

  return savedMethod ?? firstMethod;
};

// ============= REACT HOOK =============

/**
 * Centralized logic for managing payment method categories.
 * Works for both Deposit and Withdrawal.
 *
 * When `carousel` config is provided, also manages carousel store sync
 * (replaces the need for separate usePrepareMethodsForCarousel).
 */
export const useMethodGrouping = <T extends UIPaymentGroups | UIPayoutGroups>({
  methods,
  paygroupCategories,
  shouldSkipFeature,
  carousel,
}: UseMethodGroupingProps<T>): UseMethodGroupingReturn => {
  const dispatch = useAppDispatch();
  const selectedCategory = useAppSelector(selectedCategorySelector);
  const userSelections = useAppSelector(userSelectionsSelector);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Carousel refs (stable references for effects)
  const methodsRef = useRef(methods);
  methodsRef.current = methods;
  const carouselMethodListRef = useRef(carousel?.carouselMethodList || []);
  carouselMethodListRef.current = carousel?.carouselMethodList || [];
  const onMethodPreselectRef = useRef(carousel?.onMethodPreselect);
  onMethodPreselectRef.current = carousel?.onMethodPreselect;
  const lastPreselectIdRef = useRef<number | null>(null);
  const lastPreselectCategoryRef = useRef<string | null>(null);
  /** Tracks whether methodToRedirect has already been applied (initial preselect from URL) */
  const redirectAppliedRef = useRef(false);

  const categoryConfig = useMemo(
    () => parseCategoryConfig(paygroupCategories, shouldSkipFeature),
    [paygroupCategories, shouldSkipFeature],
  );

  const groupedMethodsByCategory = useMemo(() => {
    if (!categoryConfig.hasCategories) return [];
    return groupMethodsByCategoriesWithDisabled(methods, categoryConfig.categories);
  }, [methods, categoryConfig]);

  const groupedMethodsByCategoryRef = useRef(groupedMethodsByCategory);
  groupedMethodsByCategoryRef.current = groupedMethodsByCategory;

  const { visibleCategories, disabledCategory } = useMemo(() => {
    const visible = groupedMethodsByCategory.filter(
      (group) => group.categoryName !== DISABLED_CATEGORY_KEY && group.methods.length > 0,
    );
    const disabled =
      groupedMethodsByCategory.find(
        (group) => group.categoryName === DISABLED_CATEGORY_KEY && group.methods.length > 0,
      ) || null;

    return {
      visibleCategories: visible,
      disabledCategory: disabled,
    };
  }, [groupedMethodsByCategory]);

  const carouselTabs = useMemo((): TabsData[] => {
    if (!categoryConfig.useOnPW || !categoryConfig.hasCategories) {
      return [];
    }

    const allNonEmptyCategories = groupedMethodsByCategory
      .filter((group) => group.methods.length > 0)
      .sort((a, b) => a.arrangement - b.arrangement);

    return allNonEmptyCategories.map((group) => {
      const hasLostProfit = group.methods.some(
        (method) => hasLostProfitInfo(method) && method.lostProfitInfo && method.lostProfitInfo.text,
      );

      return {
        name: group.categoryName,
        badge: hasLostProfit
          ? {
              type: 'text' as const,
              value: 'PH.DEPOSIT.LABEL.CASHBACK',
            }
          : undefined,
      };
    });
  }, [categoryConfig, groupedMethodsByCategory]);

  // Auto-select first category in strict mode
  useEffect(() => {
    if (categoryConfig.isStrict && categoryConfig.useOnPW && categoryConfig.hasCategories && !selectedCategory) {
      let firstCategory;

      if (visibleCategories.length > 0) {
        firstCategory = visibleCategories[0].categoryName;
      }

      if (!firstCategory && disabledCategory?.methods?.length) {
        firstCategory = disabledCategory?.categoryName;
      }

      if (firstCategory) {
        dispatch(setSelectedCategory(firstCategory));
      }
    }
  }, [
    categoryConfig.isStrict,
    categoryConfig.useOnPW,
    categoryConfig.hasCategories,
    selectedCategory,
    visibleCategories,
    disabledCategory,
    dispatch,
  ]);

  // ============= USER SELECTIONS =============

  const selectionsMap = useMemo(
    () =>
      userSelections.reduce(
        (acc, curr) => {
          acc[curr.category_name] = curr.payGroupId;
          return acc;
        },
        {} as Record<string, number>,
      ),
    [userSelections],
  );

  const getUserSelectedGroupFn = useCallback(
    (categoryName: string) => selectionsMap[categoryName] ?? null,
    [selectionsMap],
  );

  const getUserSelectedGroupRef = useRef(getUserSelectedGroupFn);
  getUserSelectedGroupRef.current = getUserSelectedGroupFn;

  // ============= CAROUSEL SYNC (when carousel config provided) =============

  const isInCarouselMode = paygroupCategories?.is_in_carousel || false;
  const isAutoSelectBlocked = carousel?.isAutoSelectBlocked || false;

  // Sync isShowGroupingMethodsInCarousel flag
  useEffect(() => {
    if (!carousel) return;
    dispatch(setShowGroupingMethodsInCarousel(isInCarouselMode));
  }, [isInCarouselMode, carousel, dispatch]);

  // Legacy carousel: set methods from carouselMethodList.
  // Skip updates when methodToRedirect was already applied and then nullified by skipRedirectMethod.
  const carouselLockedRef = useRef(false);
  useEffect(() => {
    if (!carousel || isInCarouselMode || !carouselMethodListRef.current.length) return;
    if (carouselLockedRef.current && !carousel.methodToRedirect) return;

    dispatch(setCarouselMethods(carouselMethodListRef.current));
    carouselLockedRef.current = Boolean(carousel.methodToRedirect);
  }, [isInCarouselMode, carousel, dispatch]);

  const tryRedirectCategorySwitch = (activeMethods: Method[]): boolean => {
    if (!carousel?.methodToRedirect || !isMethodAvailable(carousel.methodToRedirect)) return false;
    if (activeMethods.find((m) => m.id === carousel.methodToRedirect?.id)) return false;

    const targetCategory = groupedMethodsByCategoryRef.current.find((group) =>
      group.methods.some((m) => m.id === carousel.methodToRedirect?.id && isMethodAvailable(m)),
    );
    if (targetCategory && targetCategory.categoryName !== selectedCategory) {
      dispatch(setSelectedCategory(targetCategory.categoryName));
      return true;
    }
    return false;
  };

  const shouldSkipCarouselPreselect = (): boolean => {
    if (selectedCategory === DISABLED_CATEGORY_KEY) return true;
    if (!selectedCategory && groupedMethodsByCategoryRef.current.length > 0) return true;
    // isAutoSelectBlocked can skip preselect, but not if methodToRedirect hasn't been applied yet.
    // This handles the case where category was switched for redirect but autoSelectTriggered
    // was set by a parallel hook before this effect could preselect the target method.
    return (
      isAutoSelectBlocked &&
      (redirectAppliedRef.current || !carousel?.methodToRedirect || !isMethodAvailable(carousel.methodToRedirect))
    );
  };

  // Grouping carousel: filter methods by category, preselect
  useEffect(() => {
    if (!carousel || !isInCarouselMode) return;

    const categoryChanged = lastPreselectCategoryRef.current !== selectedCategory;
    if (categoryChanged) {
      lastPreselectIdRef.current = null;
      lastPreselectCategoryRef.current = selectedCategory;
    }

    const activeMethods = getActiveMethods(
      selectedCategory,
      groupedMethodsByCategoryRef.current,
      paygroupCategories?.categories,
      methodsRef.current,
    );
    const sortedMethods = [...activeMethods].sort(sortByArrangement);

    if (sortedMethods.length === 0) return;

    // Carousel methods are now computed by derived selector (groupingCarouselMethodsSelector).
    // This effect only handles preselection.

    if (shouldSkipCarouselPreselect()) return;

    // After redirect method was applied, skip further preselects unless user changed category
    if (redirectAppliedRef.current && !categoryChanged) return;

    // methodToRedirect is only relevant for the initial preselect (first load from URL).
    // Once the target method is preselected, manual category changes should not re-trigger redirect logic.
    if (!redirectAppliedRef.current && tryRedirectCategorySwitch(activeMethods)) {
      return;
    }

    const effectiveRedirect = redirectAppliedRef.current ? undefined : carousel?.methodToRedirect;
    const methodToPreselect = getMethodToPreselect(
      sortedMethods,
      selectedCategory,
      activeMethods,
      getUserSelectedGroupRef.current,
      effectiveRedirect,
    );
    if (!methodToPreselect) return;

    if (lastPreselectIdRef.current === methodToPreselect.id) {
      return;
    }
    lastPreselectIdRef.current = methodToPreselect.id;

    // Mark redirect as consumed once methodToRedirect is preselected
    if (!redirectAppliedRef.current && carousel?.methodToRedirect?.id === methodToPreselect.id) {
      redirectAppliedRef.current = true;
    }

    dispatch(setSelectedPayGroupId(methodToPreselect.id));
    onMethodPreselectRef.current?.(methodToPreselect);
  }, [isInCarouselMode, selectedCategory, isAutoSelectBlocked, carousel, dispatch]);

  // ============= CATEGORY SELECTION =============

  const handleCategorySelect = useCallback(
    (categoryName: string) => {
      dispatch(setSelectedCategory(categoryName));
      dispatch(resetAutoSelectTriggered());

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      if (!categoryConfig.isStrict && categoryConfig.useOnPW && categoryConfig.hasCategories) {
        const categoryElement = document.getElementById(`category-${categoryName}`);
        if (categoryElement) {
          if (inIframe()) {
            const rect = categoryElement.getBoundingClientRect();
            const scrollTop = window.pageYOffset + rect.top;
            IntegrationCommunication.sendMessage({
              id: 'scrollTo',
              value: scrollTop,
            });
          } else {
            categoryElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }

          categoryElement.setAttribute('data-programmatic-scroll', 'true');
          scrollTimeoutRef.current = setTimeout(() => {
            categoryElement.removeAttribute('data-programmatic-scroll');
          }, SCROLL_TIMEOUT_MS);
        }
      }
    },
    [categoryConfig],
  );

  const updateSelectedCategory = useCallback(
    (category: string | null) => {
      dispatch(setSelectedCategory(category));
    },
    [dispatch],
  );

  const saveUserSelection = useCallback(
    (categoryName: string, payGroupId: number) => {
      dispatch(
        setUserCategorySelection({
          category_name: categoryName,
          payGroupId,
        }),
      );
    },
    [dispatch],
  );

  useEffect(
    () => () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    },
    [],
  );

  return {
    categoryConfig,
    groupedMethodsByCategory,
    selectedCategory,
    setSelectedCategory: updateSelectedCategory,
    carouselTabs,
    handleCategorySelect,
    visibleCategories,
    disabledCategory,
    saveUserSelection,
    getUserSelectedGroup: getUserSelectedGroupFn,
  };
};
